import type { ErpHistoryEntry } from "../../../../api/FinanceApiClient";

/** One field-level diff row ready for display: `from`/`to` are already display strings (or undefined
 * when the field had no value on that side), taken verbatim from `ErpHistoryEntry.changes`. */
export interface ErpHistoryLine {
  field: string;
  from?: string;
  to?: string;
}

/** The shape `ErpHistoryDrawer` renders per entry. `title` and `when` are plain, already-formatted
 * strings (no translation needed); `who` is the raw user name (possibly empty — the caller supplies
 * an "Unknown" fallback via `t()`). */
export interface FormattedErpHistoryEntry {
  title: string;
  when: string;
  who: string;
  lines: ErpHistoryLine[];
}

const ACTION_LABEL_KEYS: Record<ErpHistoryEntry["action"], string> = {
  Created: "HistoryCreated",
  Updated: "HistoryUpdated",
  Deleted: "HistoryDeleted",
};

/** Locale KEY for the action label (e.g. `'HistoryCreated'`) — NOT translated text. The caller
 * (`ErpHistoryDrawer`) runs this through `t()`; this module never imports i18n. */
export function describeAction(action: ErpHistoryEntry["action"]): string {
  return ACTION_LABEL_KEYS[action] ?? ACTION_LABEL_KEYS.Updated;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** `ErpChangeLog.CreatedAt` is `DateTime.UtcNow`, serialized either with a trailing `Z`/offset or
 * (per the server's plain JSON serialization elsewhere in this feature) without one — in the latter
 * case it is still UTC. Formats to local "DD/MM/YYYY HH:mm" using only the built-in `Date`, so this
 * module stays dependency-free. Falls back to the raw string if it doesn't parse. */
function formatWhen(iso: string): string {
  const hasZone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(iso);
  const d = new Date(hasZone ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Turns one raw `ErpHistoryEntry` into display-ready rows. Pure: no React, no i18n import — field
 * diffs come straight from `entry.changes` (undefined/empty for Created and Deleted, one line per
 * changed field for Updated); the action label stays a key (see `describeAction`). */
export function formatHistoryEntry(entry: ErpHistoryEntry): FormattedErpHistoryEntry {
  return {
    title: `v${entry.version}`,
    when: formatWhen(entry.createdAt),
    who: entry.userName ?? "",
    lines: (entry.changes ?? []).map(c => ({ field: c.field, from: c.oldValue, to: c.newValue })),
  };
}
